import {
  getPublicationLocales,
  type Locale,
  locales,
  parseLocale,
} from "@repo/fika-shared/src/locale"
import { difference } from "remeda"
import { createEffect, createMemo, createSignal, For } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Checkbox } from "@/designSystem/components/checkbox"
import { Form } from "@/designSystem/components/form"
import { Select } from "@/designSystem/components/select"
import { Text } from "@/designSystem/components/text"
import { RightPanel } from "@/designSystem/patterns/right-panel"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { getLanguageLabel } from "@/lib/i18n"
import { toast } from "@/lib/toast"
import { createBlogRouter } from "@/routes/posts-router"
import { usePostEditor } from "./post-editor-context"
import { PostEditorFragment } from "./post-editor-context/provider"

const UpdatePostTranslationsMutation = graphql(
  `mutation UpdatePostTranslations($postId: ID!, $mainLocale: Locale!, $locales: [Locale!]!) {
    updatePostTranslations(postId: $postId, mainLocale: $mainLocale, locales: $locales) {
      ... on MutationUpdatePostTranslationsSuccess {
        data {
          ...PostEditorFragment
        }
      }
      ...ErrorFragment
    }
  }`,
  [PostEditorFragment, ErrorFragment],
)

export function PostTranslations() {
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()
  const { post, switchToLocale } = usePostEditor()

  const [mainLocale, setMainLocale] = createSignal<Locale | null>(null)
  const [enabledLocales, setEnabledLocales] = createSignal<Set<Locale>>(
    new Set(),
  )

  const [updateState, updatePostTranslations] = createMutationWithErrorHandling(
    UpdatePostTranslationsMutation,
    "updatePostTranslations",
  )

  createEffect(() => {
    const p = post()
    if (!p) return
    setMainLocale(p.mainLocale)
    const alternatives = new Set(
      (p.availableLocales ?? [])
        .filter(l => l !== p.mainLocale)
        .map(parseLocale),
    )
    setEnabledLocales(alternatives)
  })

  const publicationLanguages = createMemo(() => {
    const p = post()
    if (!p) return locales
    const pub = p.publication
    if (!pub) return locales
    return getPublicationLocales(
      parseLocale(pub.locale),
      pub.additionalLanguages,
    )
  })

  const alternativeLocales = createMemo(() => {
    const main = mainLocale()
    if (!main) return []
    return publicationLanguages()
      .filter(l => l !== main)
      .sort((a, b) => getLanguageLabel(a).localeCompare(getLanguageLabel(b)))
  })

  const allLocaleOptions = createMemo(() =>
    publicationLanguages()
      .map(locale => ({
        value: locale,
        label: getLanguageLabel(locale),
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  )

  function handleMainLocaleChange(value: string | null) {
    if (!value) return
    const newMain = parseLocale(value)
    const prev = mainLocale()
    setMainLocale(newMain)
    setEnabledLocales(current => {
      const next = new Set(current)
      if (prev) next.add(prev)
      next.delete(newMain)
      return next
    })
  }

  function toggleLocale(locale: Locale) {
    setEnabledLocales(prev => {
      const next = new Set(prev)
      if (next.has(locale)) {
        next.delete(locale)
      } else {
        next.add(locale)
      }
      return next
    })
  }

  const isDirty = createMemo(() => {
    const p = post()
    if (!p) return false
    if (mainLocale() !== p.mainLocale) return true
    const currentAlternatives = new Set(
      (p.availableLocales ?? []).filter(l => l !== p.mainLocale),
    )
    const requested = enabledLocales()
    if (currentAlternatives.size !== requested.size) return true
    for (const l of currentAlternatives) {
      if (!requested.has(l)) return true
    }
    return false
  })

  const saveLabel = createMemo(() => {
    const p = post()
    if (!p) return LL().common.save()

    const currentAlternatives = (p.availableLocales ?? []).filter(
      l => l !== p.mainLocale,
    )
    const requested = Array.from(enabledLocales())

    const added = difference(requested, currentAlternatives)
    const removed = difference(currentAlternatives, requested)

    if (added.length > 0 && removed.length > 0) return LL().post.saveChanges()
    if (added.length > 0) return LL().post.saveAndGenerate()
    if (removed.length > 0) return LL().post.saveAndDelete()
    return LL().common.save()
  })

  async function handleSave() {
    const p = post()
    const main = mainLocale()
    if (!p || !main) return

    const result = await updatePostTranslations({
      postId: p.id,
      mainLocale: main,
      locales: Array.from(enabledLocales()),
    })

    if (result.success) {
      toast.success(LL().post.translationsSaved())
      if (main !== p.mainLocale) {
        await switchToLocale(main)
      }
    }
  }

  return (
    <RightPanel
      closeHref={paths().post().url}
      header={{
        type: "title",
        title: LL().post.translations(),
      }}
    >
      <Form
        title={LL().post.translations()}
        description={LL().post.translationsDescription()}
        hue="amber"
        iconName="languages"
        onSubmit={handleSave}
      >
        <Select
          hue="amber"
          label={LL().post.mainLanguage()}
          options={allLocaleOptions()}
          value={mainLocale}
          onChange={handleMainLocaleChange}
        />
        <Box gap="12">
          <Box gap="4">
            <Text fontSize="s" fontWeight="500">
              {LL().post.alternativeLanguages()}
            </Text>
            <Text fontSize="s" color="sand700">
              {LL().post.alternativeLanguagesDescription()}
            </Text>
          </Box>
          <For each={alternativeLocales()}>
            {locale => (
              <Checkbox
                label={getLanguageLabel(locale)}
                hue="amber"
                checked={enabledLocales().has(locale)}
                onChange={() => toggleLocale(locale)}
                disabled={updateState.fetching}
              />
            )}
          </For>
        </Box>
        <Form.Save
          hue="amber"
          disabled={!isDirty()}
          active={updateState.fetching}
          label={saveLabel()}
        />
      </Form>
    </RightPanel>
  )
}
